"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { InsightsChat, type InsightsInvestigationPickerState } from "../chat/InsightsChat";

function createThreadId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `ins-${crypto.randomUUID()}`;
  }
  return `ins-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Empty `/insights` — allocate a thread id and redirect (no chat mount = no double-run). */
export function InsightsIndexRedirect() {
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const threadId = createThreadId();
    const params = new URLSearchParams(searchParams.toString());
    const qs = params.toString();
    router.replace(`/insights/${threadId}${qs ? `?${qs}` : ""}`);
  }, [router, searchParams]);

  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      Start Investigation…
    </div>
  );
}

const DEFAULT_SUBHEADER = "Ask analytical questions across your connected systems.";

export function InsightsPage({ threadId }: { threadId: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const prompt = searchParams.get("prompt");
  const fresh = searchParams.get("fresh") === "1";
  const [investigationQuestion, setInvestigationQuestion] = useState<string | null>(null);
  const [picker, setPicker] = useState<InsightsInvestigationPickerState | null>(null);
  const [questionThreadId, setQuestionThreadId] = useState(threadId);
  if (questionThreadId !== threadId) {
    setQuestionThreadId(threadId);
    setInvestigationQuestion(null);
    setPicker(null);
  }

  const initialPrompt = useMemo(() => {
    if (!prompt?.trim()) return null;
    if (!fresh) return null;
    return prompt.trim();
  }, [fresh, prompt]);

  const onThreadReady = useCallback(
    (id: string) => {
      if (fresh || prompt) {
        router.replace(`/insights/${id}`);
      }
    },
    [fresh, prompt, router],
  );

  const subheader = investigationQuestion?.trim() || DEFAULT_SUBHEADER;
  const pickerRows = picker?.investigations.filter((inv) => inv.question.trim()) ?? [];
  const showPicker = pickerRows.length >= 2;

  return (
    <div className="flex h-full min-h-0 flex-col px-4 py-4 md:px-8">
      <header className="mb-4 shrink-0">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Insights</h1>
        {showPicker && picker ? (
          <select
            className="mt-0.5 max-w-full bg-transparent text-sm text-muted-foreground outline-none"
            value={picker.selectedInvestigationId ?? pickerRows.at(-1)?.investigationId ?? ""}
            title={investigationQuestion ?? undefined}
            onChange={(e) => {
              const id = e.target.value;
              if (id) picker.selectInvestigation(id);
            }}
          >
            {pickerRows.map((inv) => (
              <option key={inv.investigationId} value={inv.investigationId}>
                {inv.question.trim()}
              </option>
            ))}
          </select>
        ) : (
          <p
            className="line-clamp-2 text-sm text-muted-foreground"
            title={investigationQuestion ?? undefined}
          >
            {subheader}
          </p>
        )}
      </header>
      <div className="min-h-0 flex-1">
        <InsightsChat
          threadId={threadId}
          initialPrompt={initialPrompt}
          onThreadReady={onThreadReady}
          onInvestigationQuestion={setInvestigationQuestion}
          onInvestigationPickerChange={setPicker}
        />
      </div>
    </div>
  );
}
